import React from 'react';

// Design System Components
import SegmentedControl from 'Components/DesignSystem/SegmentedControl/SegmentedControl';

// Module Components
import SectionHeading from './SectionHeading';

// Constants
import { STRINGS } from '../../../constants';

// Types
import { FieldAlignment, FieldId } from '../../../types';

interface AlignmentSectionProps {
  fieldId: FieldId;
  fieldName: string;
  alignment: FieldAlignment;
  onAlignmentChange: (alignment: FieldAlignment) => void;
}

const ALIGNMENTS: { value: FieldAlignment; label: string }[] = [
  { value: 'left', label: STRINGS.pane.alignmentLeft },
  { value: 'center', label: STRINGS.pane.alignmentCenter },
  { value: 'right', label: STRINGS.pane.alignmentRight },
];

const isAlignment = (value: string): value is FieldAlignment =>
  ALIGNMENTS.some((alignment) => alignment.value === value);

/**
 * The Alignment section of a field's properties panel: Left / Center / Right,
 * exactly one selected, Left when the field is first enabled (spec R44, R66).
 *
 * The prototype's three-button segmented band, decided at the round-4 gate (Q25) —
 * drawn by the design system's `SegmentedControl`, which is a native radio group
 * underneath. So the band matches the prototype while mutual exclusivity, arrow-key
 * roving focus and the single tab stop stay the browser's own (NFR A1).
 */
const AlignmentSection = ({
  fieldId,
  fieldName,
  alignment,
  onAlignmentChange,
}: AlignmentSectionProps) => (
  <div className="tw-flex tw-flex-col tw-gap-2 tw-px-3 tw-py-3 tw-border-b tw-border-edge">
    <SectionHeading as="h4">{STRINGS.pane.alignment}</SectionHeading>

    <SegmentedControl
      data-testid={`product-card-alignment-${fieldId}`}
      ariaLabel={`${STRINGS.pane.alignment} — ${fieldName}`}
      options={ALIGNMENTS.map((option) => ({ id: option.value, label: option.label }))}
      value={alignment}
      onChange={(id) => {
        const value = String(id);
        if (isAlignment(value)) {
          onAlignmentChange(value);
        }
      }}
    />
  </div>
);

export default AlignmentSection;
